/**
 * Pessoas da viagem, no formato de avatar do design system.
 *
 * Vinha do dataset de exemplo: qualquer id desconhecido virava alguém da
 * viagem de referência, em qualquer viagem. Agora a fonte é a lista real de
 * membros — e um id que não está nela não vira uma pessoa inventada.
 *
 * É hook porque a lista de membros pertence à viagem aberta, e a viagem aberta
 * é contexto, não constante de módulo.
 */
import { useCallback } from 'react';

import { useTrip } from '../../trip/TripContext';
import type { AvatarPerson } from '../../ui/primitives';

/** Cor de reserva de quem não está em família nenhuma. */
const SEM_FAMILIA = '#FFD166';

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes.at(-1)?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

export function usePessoas() {
  const { members } = useTrip();

  const avatarDe = useCallback(
    (memberId: string): AvatarPerson => {
      const membro = members.find((m) => m.memberId === memberId);
      if (!membro) {
        return { id: memberId, name: 'Participante', initials: '?', color: SEM_FAMILIA };
      }
      return {
        id: membro.memberId,
        name: membro.name,
        initials: iniciais(membro.name),
        color: membro.familyColor ?? SEM_FAMILIA,
        url: membro.avatarUrl ?? null,
      };
    },
    [members],
  );

  /** Foto da conta e cor da família, para o `FotoAvatar` — a mesma fonte. */
  const fotoDe = useCallback(
    (memberId: string): { url: string | null; cor: string | null } => {
      const membro = members.find((m) => m.memberId === memberId);
      return { url: membro?.avatarUrl ?? null, cor: membro?.familyColor ?? null };
    },
    [members],
  );

  const nomeDe = useCallback(
    (memberId: string): string =>
      members.find((m) => m.memberId === memberId)?.name ?? 'Participante',
    [members],
  );

  return { avatarDe, fotoDe, nomeDe };
}
